import { StageChrome } from '@/routes/stage/StageChrome';
import { useStageStyles } from '@/routes/stage/useStageStyles';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { eventScreenThemeContext } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import { EngagementMode, SharedScreenState } from '@/types/screen';
import {
  EngagementType,
  ScreenElementFragment,
  StageEngagementFragment,
  TriviaSubmissionData,
  TriviaViewConfig,
  TriviaViewData,
} from '@/generated/graphql';
import { StageTriviaEngagement } from '../stage/stage-view';
import { TypedStageEngagement } from '@/utils/engagementTypeUtils';
import { StageElement } from '@/routes/stage/StageElement';
import {
  StageSlideCanvas,
  type StageSlideCanvasStyles,
} from '@/engagements/slides/stage/stageSlideCanvas.ts';
import slidesStyles from '@/engagements/slides/stage/stage-view.module.css';
import { useMemo } from 'react';

export type TriviaPreviewTheme = 'glow' | 'light';

function textElement(
  id: string,
  text: string,
  color: string,
  styles: Record<string, unknown>,
  textType: string = 'heading',
): ScreenElementFragment {
  return {
    __typename: 'ScreenElement',
    id,
    type: 'text',
    name: text,
    text,
    textType,
    color,
    imageUrl: null,
    linkHref: null,
    fontFamily: null,
    defaultClassNames: null,
    engagementClassNames: null,
    defaultStyles: styles,
    engagementStyles: styles,
  };
}

function glowStageElements() {
  const heading = {
    fontSize: '4.05vw',
    lineHeight: 1,
    whiteSpace: 'nowrap' as const,
    top: '2.3vh',
    height: '9vh',
  };
  return {
    ready: textElement('ready', 'READY.', '#ff119e', {
      ...heading,
      left: '1.15vw',
      width: '16vw',
    }),
    set: textElement('set', 'SET.', '#00E5FF', {
      ...heading,
      left: '17.4vw',
      width: '9.2vw',
    }),
    glow: textElement('glow', 'GLOW.', '#aefd45', {
      ...heading,
      left: '26.7vw',
      width: '16vw',
    }),
    sub: textElement(
      'sub',
      'My VIP and Me · Family Dance Party',
      '#e0ffff',
      {
        left: '52vw',
        top: '3.3vh',
        width: '46vw',
        height: '8vh',
        fontSize: '2.4vw',
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        textAlign: 'right',
      },
      'body',
    ),
  };
}

function lightStageElements() {
  return {
    brand: textElement('brand', 'HappiScreen', '#2a1f14', {
      left: '1.5vw',
      top: '2.5vh',
      width: '28vw',
      height: '8vh',
      fontSize: '3.2vw',
      lineHeight: 1,
      whiteSpace: 'nowrap',
    }),
    set: textElement('set', 'SET.', '#2a1f14', {
      left: '32vw',
      top: '2.5vh',
      width: '10vw',
      height: '8vh',
      fontSize: '3.2vw',
      lineHeight: 1,
      whiteSpace: 'nowrap',
    }),
    sub: textElement(
      'sub',
      'Sunday Brunch',
      '#2a1f14',
      {
        left: '78vw',
        top: '3vh',
        width: '20vw',
        height: '6vh',
        fontSize: '1.8vw',
        textAlign: 'right',
        whiteSpace: 'nowrap',
      },
      'body',
    ),
  };
}

function glowTheme(
  stageElements: SharedScreenState['savedTheme']['stageElements'],
) {
  return {
    headingFontFamily: ['Bungee', 'cursive'],
    bodyFontFamily: ['Margarine', 'cursive'],
    foregroundColor: '#ffffff',
    backgroundColor: '#0a0a12',
    stageBackgroundImage: '/trivia-preview/glow-stage-bg.jpg',
    qrWrapperBackgroundColor: '#008199',
    qrForegroundColor: '#ffffff',
    stageElements,
    fanElements: {},
  };
}

function lightTheme(
  stageElements: SharedScreenState['savedTheme']['stageElements'],
) {
  return {
    headingFontFamily: ['Playfair Display', 'serif'],
    bodyFontFamily: ['Source Serif 4', 'serif'],
    foregroundColor: '#2a1f14',
    backgroundColor: '#f4efe6',
    stageBackgroundImage: null,
    qrWrapperBackgroundColor: '#1f6f78',
    qrForegroundColor: '#ffffff',
    stageElements,
    fanElements: {},
  };
}

export function TriviaStagePreviewShell({
  engagement,
  theme,
  surface,
}: {
  engagement: TypedStageEngagement<
    TriviaViewConfig,
    TriviaViewData,
    TriviaSubmissionData
  >;
  theme: TriviaPreviewTheme;
  surface: 'trivia' | 'slides';
}) {
  const stageElements = useMemo(
    () => (theme === 'light' ? lightStageElements() : glowStageElements()),
    [theme],
  );
  const savedTheme =
    theme === 'light' ? lightTheme(stageElements) : glowTheme(stageElements);
  const activeEngagement = useMemo(() => {
    if (surface !== 'slides') return engagement as StageEngagementFragment;
    return {
      ...engagement,
      type: EngagementType.Slides,
      title: 'Family Dance Party',
      qrCodeCta: 'Scan to play!',
      viewData: {
        __typename: 'SlidesViewData',
        currentSlide: 'slide-1',
        slidesDisplayOrder: ['slide-1'],
      },
      viewConfig: {
        __typename: 'SlidesViewConfig',
        autoPlay: false,
        autoPlayIntervalMs: 8000,
      },
    } as unknown as StageEngagementFragment;
  }, [engagement, surface]);

  const state: SharedScreenState = useMemo(
    () => ({
      eventId: 'preview',
      savedTheme,
      draftTheme: {},
      selectedElementId: null,
      activeEngagement,
      engagementMode: EngagementMode.Actual,
    }),
    [activeEngagement, savedTheme],
  );

  useGoogleFonts({ fontFamily: savedTheme.headingFontFamily });
  useGoogleFonts({ fontFamily: savedTheme.bodyFontFamily });

  const { rootStyles } = useStageStyles({ state });
  const themeValue = useMemo(
    () => ({ state, dispatch: () => undefined }),
    [state],
  );

  return (
    <eventScreenThemeContext.Provider value={themeValue}>
      <div
        data-name="STAGE_ROOT"
        className="relative flex h-full w-full flex-col overflow-hidden"
        style={rootStyles}
      >
        <StageChrome name="STAGE_CHROME">
          {Object.keys(savedTheme.stageElements).map((elementId) => (
            <StageElement
              key={elementId}
              elementId={elementId}
              editor={false}
            />
          ))}
          {surface === 'slides' ? (
            <div data-slides-stage className="h-full min-h-0 w-full">
              <StageSlideCanvas
                currentSlideId="slide-1"
                slideData={{
                  title: 'Family Dance Party',
                  content:
                    '# Family Dance Party\n\nGrab a glow stick. The floor is yours.',
                }}
                styles={slidesStyles as StageSlideCanvasStyles}
              />
            </div>
          ) : (
            <StageTriviaEngagement engagement={engagement} />
          )}
        </StageChrome>
      </div>
    </eventScreenThemeContext.Provider>
  );
}
